import { ApiResponse, ResponseError } from './openapi';

export class APIError extends Error {
  constructor(
    message: string,
    public context: ResponseError,
    public init: HandleRequest
  ) {
    super(message)
  }
}

export class HandleRequest implements RequestInit {
  loading?: (loading: boolean) => void
  id?: string;
  name?: string;
  broadcast?: boolean;
  event?: boolean;
  sequence?: boolean = true;
  cacheTime?: number;
  debounce?: number;
  timeoutInMs?: number;
  onError?: <T>(context: ResponseError, init: HandleRequest) => Promise<ApiResponse<T>>;
  group?: string = '';
  retry = {
    maxRetries: 3,
    timeoutMs: 1000,
    excludedStatusCodes: [400, 401, 404, 500]
  };



  /** A BodyInit object or null to set request's body. */
  body?: BodyInit | null;
  /** A string indicating how the request will interact with the browser's cache to set request's cache. */
  cache?: RequestCache;
  /** A string indicating whether credentials will be sent with the request always, never, or only when sent to a same-origin URL. Sets request's credentials. */
  credentials?: RequestCredentials;
  /** A Headers object, an object literal, or an array of two-item arrays to set request's headers. */
  headers?: HeadersInit;
  /** A cryptographic hash of the resource to be fetched by request. Sets request's integrity. */
  integrity?: string;
  /** A boolean to set request's keepalive. */
  keepalive?: boolean;
  /** A string to set request's method. */
  method?: string;
  /** A string to indicate whether the request will use CORS, or will be restricted to same-origin URLs. Sets request's mode. */
  mode?: RequestMode;
  priority?: RequestPriority;
  /** A string indicating whether request follows redirects, results in an error upon encountering a redirect, or returns the redirect (in an opaque fashion). Sets request's redirect. */
  redirect?: RequestRedirect;
  /** A string whose value is a same-origin URL, "about:client", or the empty string, to set request's referrer. */
  referrer?: string;
  /** A referrer policy to set request's referrerPolicy. */
  referrerPolicy?: ReferrerPolicy;
  /** An AbortSignal to set request's signal. */
  signal?: AbortSignal | null;
  /** Can only be null. Used to disassociate request from any Window. */
  window?: null;


  private static sequencer: {
    list: (() => Promise<unknown>)[],
    running: boolean,
    run: () => Promise<void>
  } = {
      list: [],
      running: false,
      async run() {
        if (this.running)
          return;
        this.running = true;
        while (this.list.length > 0) {
          let current = this.list.shift();
          if (current)
            await current();
        }
        this.running = false;

      }
    };
  private static debounceTimer: { [key: string]: NodeJS.Timeout } = {};
  private static debounceResolves: { [key: string]: [(value: any | PromiseLike<any>) => void, (reject: any) => void][] } = {};
  private static CacheStorage: { [key: string]: unknown } = {}
  private static Sequence<A, T>(callback: (arg: A, options?: HandleRequest) => Promise<T>, options: HandleRequest): (arg: A, options?: HandleRequest) => Promise<T> {
    return async (_arg: A, _options?: HandleRequest) => {
      return new Promise((resolve) => {
        this.sequencer.list.push(async () => {
          let resp = await callback(_arg, _options ?? options);

          resolve(resp)
        });
        this.sequencer.run();
      })
    }
  }
  private static Loading<A, T>(callback: (arg: A, options?: HandleRequest) => Promise<T>, options: HandleRequest): (arg: A, options?: HandleRequest) => Promise<T> {
    return async (_arg: A, _options?: HandleRequest) => {
      if (options.loading)
        options.loading(true)
      return await callback(_arg, _options ?? options).finally(() => {
        if (options.loading)
          options.loading(false)
      });
    }
  }

  private static Debounce<A, T>(callback: (arg: A, options?: HandleRequest) => Promise<T>, options: HandleRequest): (arg: A, options?: HandleRequest) => Promise<T> {
    return (_arg: A, _options?: HandleRequest) => {
      let key = options.id ?? crypto.randomUUID();
      if (this.debounceResolves[key] == null)
        this.debounceResolves[key] = [];
      if (this.debounceTimer[key])
        clearTimeout(this.debounceTimer[key]);


      this.debounceTimer[key] = setTimeout(async () => {
        let result: unknown;
        let error: unknown;
        await callback(_arg, _options ?? options).then(res => result = res, err => error = err);
        this.debounceResolves[key].forEach(r => result != null ? r[0](result) : r[1](error))
        this.debounceResolves[key] = [];
      }, (_options ?? options).debounce ?? 750);


      return new Promise((res, rej) => this.debounceResolves[key].push([res, rej]));
    }
  }


  private static Timeout<A, T>(callback: (arg: A, options?: HandleRequest) => Promise<T>, options: HandleRequest): (arg: A, options?: HandleRequest) => Promise<T> {
    let reason = 'Timeout';
    return (_arg: A, _options?: HandleRequest) => new Promise<T>(_resolve => {
      let abortController = new AbortController();
      (_options ?? options).signal = abortController.signal;
      let timeoutPromise = new Promise<T>((resolve, reject) => setTimeout(() => {
        abortController.abort(reason);
        return reject;
      }, (_options ?? options).timeoutInMs, reason))
      _resolve(Promise.race<T>([callback(_arg, _options ?? options), timeoutPromise]))
    })
  }
  private static Retry<A, T>(callback: (arg: A, options?: HandleRequest) => Promise<ApiResponse<T>>, options: HandleRequest): (arg: A, options?: HandleRequest) => Promise<ApiResponse<T>> {
    return (_arg: A, _options?: HandleRequest) => new Promise<ApiResponse<T>>(async (resolve, reject) => {
      let retries = 0;
      let response = null;
      let error: Response | null | ResponseError = null
      // @ts-ignore
      while (response == null && !options.retry.excludedStatusCodes.includes(error?.status ?? '') && retries < options.retry.maxRetries) {
        error = null
        try {
          response = await callback(_arg, _options ?? options).catch(err => error = err);
        } catch (e) {
          error = e as ResponseError
        }

        if (error != null && options.retry.excludedStatusCodes.includes(error?.response?.status ?? ''))
          break;
        else if (error != null)
          await new Promise(_resolve => setTimeout(_resolve, options.retry.timeoutMs * (retries + 1)));
        if (response == error)
          response = null;
        retries++;
      }
      if (error != null && options.onError != null)
        resolve(options.onError(error, options))

      resolve(response);
    })
  }
  private static Broadcast<A, T>(callback: (arg: A, options?: HandleRequest) => Promise<T>, options: HandleRequest,): (arg: A, options?: HandleRequest) => Promise<T> {
    return async (_arg: A, _options?: HandleRequest) => {
      let response = await callback(_arg, _options ?? options)
      if ('BroadcastChannel' in globalThis && options.group) {
        let broadcastChannel = new BroadcastChannel(options.group);
        broadcastChannel.postMessage(response);
        broadcastChannel.close();
      }
      return response;
    }
  }
  private static Event<A, T>(callback: (arg: A, options?: HandleRequest) => Promise<T>, options: HandleRequest,): (arg: A, options?: HandleRequest) => Promise<T> {
    return (_arg: A, _options?: HandleRequest) => new Promise<T>(async (resolve, reject) => {
      let response = await callback(_arg, _options ?? options)
      if (options.group)
        globalThis.dispatchEvent(new CustomEvent<T>(options.group, { detail: response }));
      resolve(response);
    })
  }


  private static Cache<A, T>(callback: (arg: A, options?: HandleRequest) => Promise<T>, options: HandleRequest,): (arg: A, options?: HandleRequest) => Promise<T> {
    return async (_arg: A, _options?: HandleRequest) => {
      if (options.group) {
        let key = options.group + JSON.stringify(_arg);
        if (this.CacheStorage[key])
          return this.CacheStorage[key] as T;
        let response = await callback(_arg, _options ?? options);
        this.CacheStorage[key] = response;
        setTimeout(() => delete this.CacheStorage[key], options.cacheTime ?? 2000);
        return response as T;
      }
      return null as T;
    }
  }

  /**
   * @param {Function} callback - The callback returns a promise
   * @param {HandleRequest} Options
   * @return {Function} - Wrapped function to call
   */
  static Handle<A, T>(id: string = crypto.randomUUID(), callback: (arg: A, options?: HandleRequest) => Promise<ApiResponse<T>>,): (arg: A, options?: Partial<HandleRequest>) => Promise<T> {
    // @ts-ignore
    return (arg: A, options?: HandleRequest) => {
      options = new HandleRequest(options);
      options.id = options?.id ?? id;
      options.name =  id;
      try {
        let _callback = callback;
        if (options.timeoutInMs)
          _callback = this.Timeout(_callback, options);
        if (options.retry)
          _callback = this.Retry(_callback, options);
        let __callback = async (arg: A, options?: HandleRequest) => await _callback(arg, options).then(async _ => await _.value())
        if (options.broadcast)
          __callback = this.Broadcast(__callback, options);
        if (options.event)
          __callback = this.Event(__callback, options);
        if (options.sequence)
          __callback = this.Sequence(__callback, options);
        if (options.loading)
          __callback = this.Loading(__callback, options);
        if (options.cacheTime)
          __callback = this.Cache(__callback, options);
        if (options.debounce)
          __callback = this.Debounce(__callback, options);
        return __callback(arg, options)
      } catch (e) {
        console.error(e)
        if (options.onError)
          options.onError(e as ResponseError, options);
      }
      return Promise.resolve(null as T)
    }
    // return this<T>.Cache(this.Debounce(this.Retry(this.Timeout(this.Broadcast(callback:(arg: A, options?: HandleRequest) => Promise<ApiResponse<T>>, options), options), options), options), option: (arg: A, options?: HandleRequest) => Promise<ApiResponse<T>>s)
  }

  constructor(req?: Partial<HandleRequest>) { Object.assign(this, req || {}); }
}
